import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import XLSX from 'xlsx'

import { GET, httpOk } from 'helpers/api'
import { returnSign, formatDate, formatMoney } from 'helpers/helpers'
import CustomTooltip from "pages/parts/CustomTooltip"
import PeriodPicker from 'pages/parts/PeriodPicker'
import { docsPageUrl } from "helpers/apps"
import { businessToday } from 'helpers/businessDay'

// Разделы отчёта сверху вниз — корни плана счетов
const SECTIONS = [
	{ rootId: 1, label: 'bs_assets' },
	{ rootId: 4, label: 'bs_liabilities' },
	{ rootId: 3, label: 'bs_capital', hint: 'bs_capital_hint' },
]

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

// По умолчанию — с начала года по сегодня, по месяцам (как «Меню по месяцам»)
function defaultFilter(posId) {
	const today = businessToday()
	return {
		start_date: today.substring(0, 4) + '-01-01',
		end_date: today,
		pos_id: posId || -1,
		detail: 'month',
	}
}

function BalanceSheet() {
	const { t, i18n } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const details = [
		{ id: 'day', name: t('pl_detail_day') },
		{ id: 'month', name: t('pl_detail_month') },
	]

	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState(defaultFilter(reduxAccount.pos_id))
	const [appliedFilter, setAppliedFilter] = useState(null)
	const [data, setData] = useState({ columns: [], accounts: [], net_assets: [] })
	const [showEmpty, setShowEmpty] = useState(false)
	// Как в P&L: всё раскрыто сразу, хранится только свёрнутое
	const [collapsed, setCollapsed] = useState({})
	const [poses, setPoses] = useState([])

	async function searchWithFilter(nextFilterData = null) {
		const currentFilterData = nextFilterData || filterData
		var urlParams = ""
		if (currentFilterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + currentFilterData.pos_id
		if (currentFilterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + currentFilterData.start_date
		if (currentFilterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + currentFilterData.end_date
		if (currentFilterData.detail)
			urlParams += returnSign(urlParams) + 'detail=' + currentFilterData.detail

		const response = await GET(`/report-balance-sheet${urlParams}`, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setAppliedFilter(currentFilterData)
			setDropdownIsOpen(false)
		}
	}

	const columns = data.columns || []

	// Пустой счёт — без остатка во всех колонках (или везде ноль)
	function isEmpty(account) {
		return account.balances.every(value => value === null || Number(value) === 0)
	}

	// Дерево раздела: строки по порядку обхода, с уровнем вложенности
	function buildRows(rootId) {
		const byParent = {}
		data.accounts.forEach(account => {
			if (account.parent_id === null) return
			if (!byParent[account.parent_id]) byParent[account.parent_id] = []
			byParent[account.parent_id].push(account)
		})

		const rows = []
		const walk = (parentId, level, hidden) => {
			(byParent[parentId] || []).forEach(account => {
				// Скрытый в плане счетов пустой счёт не показываем и с «Показывать пустые счета»
				if ((!showEmpty || account.is_hidden) && isEmpty(account)) return
				rows.push({ ...account, level, hidden })
				if (account.has_children) {
					walk(account.id, level + 1, hidden || collapsed[account.id])
				}
			})
		}
		walk(rootId, 0, collapsed['root_' + rootId])
		return rows
	}

	function rootOf(rootId) {
		return data.accounts.find(account => account.id === rootId)
	}

	function columnLabel(column) {
		if (appliedFilter?.detail === 'day') return formatDate(column.date)
		// Год не пишем: он уже есть в дате под месяцем («на 31.01.2026»)
		const month = column.period.split('-')[1]
		return t(MONTHS[Number(month) - 1])
	}

	// Текущий месяц обрывается на сегодня — подписываем, на какую дату остаток
	function columnSubLabel(column) {
		if (appliedFilter?.detail === 'day') return column.is_today ? t('today') : ''
		return t('bs_on_date', { date: formatDate(column.date) })
	}

	function toggle(key) {
		setCollapsed({ ...collapsed, [key]: !collapsed[key] })
	}

	// Свернуть всё, если хоть что-то раскрыто, иначе раскрыть всё
	function toggleAll() {
		const keys = SECTIONS.map(section => 'root_' + section.rootId)
		data.accounts.forEach(account => {
			if (account.has_children && account.parent_id !== null) keys.push(account.id)
		})
		const anyOpen = keys.some(key => !collapsed[key])
		const next = {}
		keys.forEach(key => { next[key] = anyOpen })
		setCollapsed(next)
	}

	function exportToExcel() {
		const header = [t('account'), t('code')]
		columns.forEach(column => header.push(columnLabel(column) + ' (' + formatDate(column.date) + ')'))
		const sheet = [header]
		const cells = (values) => values.map(value => value === null || value === undefined ? '' : Number(value) || 0)
		SECTIONS.forEach(section => {
			const root = rootOf(section.rootId)
			if (!root) return
			sheet.push([t(section.label).toUpperCase(), '', ...cells(root.balances)])
			buildRows(section.rootId).forEach(row => {
				sheet.push(['   '.repeat(row.level + 1) + row.name, row.code || '', ...cells(row.balances)])
			})
		})
		sheet.push([t('bs_net_assets').toUpperCase(), '', ...cells(data.net_assets || [])])

		const ws = XLSX.utils.aoa_to_sheet(sheet);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('balance_sheet') + ".xlsx");
	}

	function resetFilter() {
		const nextFilterData = defaultFilter(reduxAccount.pos_id)
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses([{ id: -1, name: t('all_poses') }, ...response.data])
	}

	useEffect(() => {
		getPoses()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	function balanceClass(value) {
		return Number(value) < 0 ? 'text-danger' : ''
	}

	function renderValues(values, bold) {
		return values.map((value, index) => (
			<td key={index} className={`text-end text-nowrap ${balanceClass(value)}`}>
				{value === null || value === undefined ? '' : (bold ? <b>{formatMoney(value)}</b> : formatMoney(value))}
			</td>
		))
	}

	function renderSection(section) {
		const root = rootOf(section.rootId)
		if (!root) return null
		const rootKey = 'root_' + section.rootId
		return (
			<React.Fragment key={rootKey}>
				<tr className="table-light">
					<td className="text-center cursor" onClick={() => toggle(rootKey)}>
						<i className={`uil ${collapsed[rootKey] ? 'uil-plus' : 'uil-minus'}`}></i>
					</td>
					<td>
						<b className="text-uppercase">{t(section.label)}</b>
						{section.hint &&
							<div className="text-muted fz-12">{t(section.hint)}</div>
						}
					</td>
					{renderValues(root.balances, true)}
				</tr>
				{buildRows(section.rootId).map(row => (
					<tr key={row.id} className={row.hidden ? 'd-none' : ''}>
						<td></td>
						<td className={row.has_children ? 'cursor' : ''}
							style={{ 'paddingLeft': (16 + row.level * 18) + 'px' }}
							onClick={() => row.has_children && toggle(row.id)}>
							{row.has_children &&
								<i className={`uil ${collapsed[row.id] ? 'uil-angle-right' : 'uil-angle-down'} me-1`}></i>
							}
							{row.has_children ? <b>{row.name}</b> : row.name}
							{row.code &&
								<span className="text-muted fz-12 ms-2">{row.code}</span>
							}
						</td>
						{renderValues(row.balances, row.has_children)}
					</tr>
				))}
			</React.Fragment>
		)
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center h-100">
					{t('balance_sheet')}
				</h4>
				<a className="btn-rounded btn btn-outline-primary text-nowrap" target="_blank" rel="noreferrer"
					href={docsPageUrl('finance_balance_sheet', i18n.language)}>
					<i className="uil uil-book-open me-1"></i>{t('documentation')}
				</a>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('pl_detail_date')}</label>
												<Select
													options={details}
													value={details.find(option => option.id === filterData.detail)}
													onChange={(option) => setFilterData({ ...filterData, 'detail': option.id })}
													placeholder=""
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
												/>
												<div className="text-muted fz-12 mt-1">{t('bs_period_hint')}</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter()}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2"
										onClick={() => exportToExcel()}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
							<div className="d-flex align-items-center ms-2">
								<input className="form-check-input m-0 me-2" type="checkbox" id="bs-show-empty"
									checked={showEmpty}
									onChange={(e) => setShowEmpty(e.target.checked)} />
								<label className="form-check-label text-nowrap" htmlFor="bs-show-empty">{t('bs_show_empty')}</label>
							</div>
						</div>
					</div>

					{appliedFilter &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								<span className="me-2">
									<b>{t('period')}: </b>
									<span>{formatDate(appliedFilter.start_date)}</span>
									<span> | </span>
									<span>{formatDate(appliedFilter.end_date)}</span>
								</span>
								<span className="me-2">
									<b>{t('pos')}: </b>
									{poses.find(pos => pos.id === appliedFilter.pos_id)?.name}
								</span>
								<span className="me-2">
									<b>{t('pl_detail_date')}: </b>
									{details.find(option => option.id === appliedFilter.detail)?.name}
								</span>
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered pl-table mb-0">
							<thead>
								<tr>
									<th className="text-center cursor" style={{ 'minWidth': '33px' }} onClick={() => toggleAll()}
										title={t('pl_toggle_all')}>
										<i className="uil uil-sort"></i>
									</th>
									<th style={{ 'minWidth': '320px' }}>{t('account')}</th>
									{columns.map((column, index) => (
										<th key={index} className="text-end text-nowrap">
											{columnLabel(column)}
											{columnSubLabel(column) &&
												<div className="text-muted fz-12 fw-normal">{columnSubLabel(column)}</div>
											}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{SECTIONS.map(section => renderSection(section))}
								{columns.length > 0 &&
									<tr className="table-active">
										<td></td>
										<td>
											<b className="text-uppercase">{t('bs_net_assets')}</b>
											<div className="text-muted fz-12">{t('bs_net_assets_hint')}</div>
										</td>
										{renderValues(data.net_assets || [], true)}
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default BalanceSheet
